<template>
    <div class="app-translation-editor">
    <app-error
        v-if="hasErrors"
        class="is--inline"
        v-bind="errors" />
    <app-error
        v-if="hasWarnings"
        class="is--warning is--inline"
        v-bind="warnings" />
        <div class="app-translation-editor__header col-12">
            <div class="app-translation-editor__header-titles">
                <div class="app-tab-navigation">
                    <div class="app-tab-navigation__breadcrumbs">
                        <a class="app-tab-navigation__breadcrumb" :href="`/projects/${project.id}/translations`">
                            <svg class="element-icon--breadcrumb element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-arrow-left"></use></svg>
                            Back to all translations
                        </a>
                    </div>
                </div>
                <h2 class="app-translation-editor__header-title">Edit Translation: {{ locale.language_code + ' (' + locale.locale + ')' }} </h2>
            </div>
            <div class="app-translation-editor__header-buttons">
                <button v-if="!hasBasicAccess" class="element-button--secondary element-button"
                    @click="exportToggle()"
                    :disabled="status">
                    Export
                </button>
                <button v-if="!hasBasicAccess" class="element-button--secondary element-button"
                    @click="importToggle()"
                    :disabled="status">
                    Import
                </button>
                <a class="element-button--secondary element-button"
                    :href="manageAssetsUrl"
                    :disabled="status">
                    Manage Assets
                </a>
                <button 
                    type="button"
                    class="element-button--primary element-button"
                    @click="saveAll()"
                    :disabled="status">
                    {{ status === 'saving' ? 'Saving...' : 'Save All' }}
                </button>
            </div>
        </div>

        <div class="page-main__section grid">
            <h3 class="page-main__section-title col-12">Locale Info</h3>
            <div class="col-6">
                <div class="input text">
                    <input type="text" v-model="locale.locale" placeholder="Language code (es-es)" :disabled="status">
                </div>
            </div>
            <div class="col-6">
                <div class="input text">
                    <input type="text" v-model="locale.language_code" placeholder="Language (Spanish)" :disabled="status">
                </div>
            </div>

            <div class="app-toast message is--success" v-if="message">
                <div class="app-toast__text">
                    <p class="app-toast__title">{{ message }}</p>
                </div>
            </div>
        </div>

        <div class="app-filters">
            <div class="app-filters__titles">
                <h3 class="app-filters__title">Text Translations</h3>
            </div>
            <div class="app-filters__filters">
                <translation-sync-keys
                    :project="project"
                    :disabled="status">
                </translation-sync-keys>
                <button v-if=aiEnabled class="element-button--ai-primary element-button"
                    @click="sendToAiTranslator()"
                    :disabled="status">
                    <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
                    Translate Using AI
                </button>
                <translation-importer
                    v-show="showImport"
                    :locale="locale"
                    :project="project"
                    @cancel="importToggle"
                    @error="setError"
                    @success="importSuccess"
                    class="">
                </translation-importer>
                <translation-exporter
                    v-show="showExport"
                    :locale="locale"
                    :project="project"
                    :export-assets-url="exportAssetsUrl"
                    :export-dictionary-url="exportDictionaryUrl"
                    @cancel="exportToggle"
                    class="">
                </translation-exporter>
            </div>
        </div>
        <div class="element-table__wrapper">
            <table>
                <thead>
                    <tr>
                        <th>English Version</th>                    
                        <th> {{ locale.language_code + ' (' + locale.locale + ')' }} Version
                            <div data-bs-toggle="dropdown" class="element-chip is--ai">
                                <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
                                AI
                            </div>
                            <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                                <h4 class="app-tooltip__title">AI Generated</h4>
                                <div class="app-tooltip__body">
                                <p>We’ve used AI to generate this content based on the information provided.</p>
                                </div>
                            </div>
                        </th>
                        <th>Used Count</th>
                        <th>Reference Locations</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, index) in dictionary" :key="`key-${index}`">
                        <td class="app-translation-editor__english-version" width="40%">
                            {{ item[0] }}
                        </td>
                        <td width="40%">
                            <div class="input textarea">
                                <textarea
                                    ref="editor"
                                    type="text"
                                    class="form-control mr-2"
                                    v-model="item[1]"
                                    :disabled="status"></textarea>
                            </div>
                        </td>
                        <td>
                            <span v-if="!isUnused(item[0])">
                                {{ getUsageCount(item[0]) }}
                            </span>
                            <span v-if="isUnused(item[0])" class="element-badge is--error">
                                Unused
                            </span>
                        </td>
                        <td width="15%" class="app-translation-editor__links">
                            <button v-if="!links[item[0]] && !isUnused(item[0])"
                                type="button"
                                class="element-button--secondary element-button"
                                @click="getUsageLinks($event, item[0])"
                                :disabled="status">
                                Show Links
                            </button>
                            <button v-if="isUnused(item[0]) && !hasBasicAccess"
                                type="button"
                                class="element-button--secondary element-button"
                                @click="deleteItem(item[0])"
                                :disabled="status">
                                Delete
                            </button>
                            <span>
                                <a v-for="(link, i, index) in links[item[0]]" :href="link" class="element-button--ghost element-button" target="_blank">{{ (i ?? 0) + 1 }}</a>
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import api from '../api/translations'
import Echo from '../helpers/echo'
import { mapGetters } from 'vuex'

export default {
    components: {

    },
    props: {
        project: {
            type: Object,
            default: null
        },
        locale: {
            type: Object,
            default: null
        },
        dictionary_keys: {
            type: Array,
            default: null
        },
        exportDictionaryUrl: {
            type: String,
            default: null
        },
        exportAssetsUrl: {
            type: String,
            default: null
        },
        importDictionaryUrl: {
            type: String,
            default: null
        },
        manageAssetsUrl: {
            type: String,
            default: null
        },
        aiEnabled: {
            type: [Boolean, Number],
            default: false
        },
    },
    data () {
        return {
            localeDictionary: this.locale?.dictionary ?? [],
            message: null,
            showImport: false,
            showExport: false,
            unusedKeys: [],
            links: {},
            errors: [],
            warnings: [],
            status: null
        }
    },
    computed: {
        ...mapGetters([
            'hasBasicAccess'
        ]),
        dictionary () {
            let dict = []

            // generate directory in correct order, and include the translation
            if (this.dictionary_keys?.length) {
                _.each(this.dictionary_keys, ({ key, id }) => {
                    const translation = this.getTranslationForKey(key)
                    const entry = [
                        key,
                        translation
                    ]

                    dict.push(entry)
                })
            }

            // append UNUSED keys currently saved in the DB
            if (this.localeDictionary?.length)
            {
                _.each(this.localeDictionary, ( item ) => {

                    const key = item[0]
                    const found = dict.find(item => item[0].trim() === key.trim()) ?? false

                    if (!found)
                    {
                        const translation = item[1] ?? ''
                        const entry = [
                            key,
                            translation
                        ]
    
                        dict.push(entry)
                        this.unusedKeys.push(key)
                    }
                })
            }

            return dict
        },
        hasDownloadableZip () {
            return this.locale?.assets_export_zip && this.locale?.assets_exported_at
        },
        downloadAssetsUrl () {
            return this.exportAssetsUrl + "?download=1"
        },
        hasErrors () {
            return this.errors?.message ?? this.errors?.errors?.message
        },
        hasWarnings () {
            if (this.unusedKeys?.length)
            {
                this.warnings = { message:'Dictionary contains unused keys, remove them and save'} 
                return true
            }
            return false
        }
    },
    methods: {
        getTranslationForKey(key) {
            const dict = this.localeDictionary ?? []
            const found = dict.find(item => item[0] === key) ?? []
            return found[1] ?? ''
        },
        isUnused(key) {
            return this.unusedKeys.includes(key)
        },
        getUsageCount(key) {
            if (this.dictionary_keys?.length) {
                const found = _.find(this.dictionary_keys, { 'key': key } )

                if (found)
                    return found.use_count ?? 0
            }

            return 0
        },
        async getUsageLinks(event, key) {
            let links = [];

            const dictionaryKey = _.find(this.dictionary_keys, { 'key': key } )
            const keyId = dictionaryKey?.id ?? null

            if (!keyId)
                return links

            const result = await api.getDictionaryKeyLinks(this.project.id, keyId)

            if (result)
                this.links[key] = result ?? []

            return links
        },
        async saveAll () {
            this.status = 'saving'
            this.errors = []

            try {
                this.message = null
                const data = {
                    "locale": this.locale.locale,
                    "language_code": this.locale.language_code,
                    "dictionary": this.dictionary,
                }
                const result = await api.saveTranslations(this.project.id, this.locale.id, data)

                if (result.errors) {
                    // catch errors
                    this.errors = result
                }
                else {
                    this.message = 'Successfully updated.'
                }
            } catch (err) {
                console.error(err)
                this.errors = _.get(err, 'response.errors', [])
            }

            this.status = null
        },
        async sendToAiTranslator () {
            //this.status = 'saving'
            this.errors = []
            
            try {
                this.message = null
                const data = {
                    "locale": this.locale.locale,
                    "language_code": this.locale.language_code,
                    "dictionary": this.dictionary,
                }
                const result = await api.sendToAiTranslator(this.project.id, this.locale.id, data)

                if (result.errors) {
                    // catch errors
                    this.errors = result.errors
                }
                else {
                    this.message = result.status ?? 'Successfully updated.'
                }
            } catch (err) {
                console.error(err)
                this.errors = _.get(err, 'response.errors', [])
            }

            this.status = null
        },
        async refreshData() {
            try {
                const result = await api.refresh(this.project.id, this.locale.id)
                const model = _.get(result, 'locale')
                if (model.dictionary)
                    this.localeDictionary = model.dictionary
            } catch (error) {
                console.error('Error refreshing data:', error)
            }
        },
        setupEchoListener () {
            const uId = window?.userData?.user?.id ?? null
            if (uId)
            {
                Echo.private("App.Models.User." + uId)
                    .notification((notification) => {
                        if (notification?.extra?.action === 'translation-ai-sync') {
                            this.refreshData()
                        }
                    })
            }
        },
        deleteItem(key) {
            this.unusedKeys = _.without(this.unusedKeys, key)
            this.localeDictionary = _.reject(this.localeDictionary, function(item) {
                return item[0] === key
            })
        },
        importToggle(status) {
            this.showImport = status !== undefined ? status : !this.showImport
        },
        exportToggle(status) {
            console.log(status)
            this.showExport = status !== undefined ? status : !this.showExport
        },
        setError (errors) {
            this.errors = errors
        },
        importSuccess(locale) {
            this.errors = []
            this.message = 'Successfully updated.'
            this.showImport = false
            this.localeDictionary = locale.dictionary
        }
    },
    created () {

    },
    mounted () {
        this.setupEchoListener()
    }
}
</script>
